import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api-client";
import { Umbrella, Lock, FileText, Clock, Heart, ShieldAlert } from "lucide-react";

export default function BeneficiaryPortal() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [loading, setLoading] = useState(true);
  const [portal, setPortal] = useState(null);
  const [docs, setDocs] = useState([]);
  const [openDocId, setOpenDocId] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setError("Missing beneficiary token. Use the link from your notification email.");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const p = await api.post("/beneficiary/verify", { token }).then((r) => r.data);
        if (cancelled) return;
        setPortal(p);
        const v = await api.post("/beneficiary/vault", { token }).then((r) => r.data);
        if (cancelled) return;
        setDocs(v.documents || []);
      } catch (e) {
        const detail = e?.response?.data?.detail || "Portal unavailable";
        setError(detail);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] text-white/70 flex items-center justify-center font-mono-uc text-sm" data-testid="beneficiary-loading">
        Opening portal…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] text-white/80 flex items-center justify-center p-6" data-testid="beneficiary-error">
        <div className="max-w-md text-center border border-red-500/30 p-8">
          <ShieldAlert size={32} className="mx-auto text-red-400 mb-3"/>
          <div className="font-serif-display text-2xl mb-2">Portal Not Available</div>
          <div className="text-white/60 text-sm font-mono-uc">{error}</div>
          <div className="text-white/40 text-[11px] mt-4 font-mono-uc">
            The Beneficiary Portal is only accessible after the account holder's Dead Man's Switch has fired.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white/85" data-testid="beneficiary-portal">
      {/* Header */}
      <div className="border-b border-white/10 py-6 px-6 md:px-12 bg-gradient-to-b from-red-950/20 to-transparent">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <Umbrella size={22} className="text-amber-400"/>
          <div>
            <div className="font-mono-uc text-[10px] uppercase tracking-[0.3em] text-red-300/80">BENEFICIARY PORTAL · READ-ONLY</div>
            <div className="font-serif-display text-2xl">The Sovereign Umbrella Command</div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6 md:p-12 space-y-8">
        {/* Legacy Note — first thing the beneficiary sees */}
        <section className="border border-amber-500/20 bg-amber-500/5 p-6" data-testid="beneficiary-legacy-note">
          <div className="flex items-center gap-2 mb-3">
            <Heart size={14} className="text-amber-400"/>
            <span className="uc-chip">MESSAGE FROM {portal.owner_name || portal.owner_email}</span>
          </div>
          {portal.legacy_note ? (
            <div className="font-serif-display text-lg leading-relaxed whitespace-pre-wrap">
              {portal.legacy_note}
            </div>
          ) : (
            <div className="text-white/50 italic text-sm">The account holder did not leave a personal message.</div>
          )}
          <div className="font-mono-uc text-[10px] text-white/40 mt-4 pt-3 border-t border-white/10 flex justify-between">
            <span>Portal opened for {portal.contact_name || "you"}</span>
            {portal.triggered_at && <span>Triggered {new Date(portal.triggered_at).toLocaleString()}</span>}
          </div>
        </section>

        {/* Executed Vault (read-only) */}
        <section data-testid="beneficiary-executed-vault">
          <div className="flex items-center gap-2 mb-3">
            <Lock size={14} className="text-white/60"/>
            <div className="font-mono-uc text-[10px] uppercase tracking-widest text-white/60">
              Executed Documents · {portal.executed_document_count}
            </div>
          </div>

          {docs.length === 0 ? (
            <div className="border border-dashed border-white/10 p-6 text-center text-white/40 text-sm italic">
              No executed documents in the vault yet.
            </div>
          ) : (
            <div className="space-y-2">
              {docs.map((d) => (
                <div key={d.id} className="border border-white/10 hover:border-white/20 transition-colors" data-testid={`beneficiary-doc-${d.id}`}>
                  <button
                    className="w-full flex items-start gap-3 p-4 text-left"
                    onClick={() => setOpenDocId(openDocId === d.id ? null : d.id)}
                  >
                    <FileText size={16} className="text-amber-400 mt-0.5 flex-none"/>
                    <div className="flex-1 min-w-0">
                      <div className="font-serif-display text-lg">{d.title}</div>
                      <div className="font-mono-uc text-[10px] text-white/40 mt-1 flex items-center gap-3 flex-wrap">
                        <span className="flex items-center gap-1"><Clock size={9}/> {new Date(d.created_at).toLocaleString()}</span>
                        {d.source_kind && <span>· {d.source_kind}</span>}
                        {d.signature_name && <span>· signed by {d.signature_name}</span>}
                      </div>
                    </div>
                  </button>
                  {openDocId === d.id && (
                    <div className="p-4 border-t border-white/10 bg-black/30 whitespace-pre-wrap font-mono-uc text-[13px] leading-relaxed text-white/80 max-h-96 overflow-y-auto" data-testid={`beneficiary-doc-body-${d.id}`}>
                      {d.content}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="text-center text-white/30 font-mono-uc text-[10px] tracking-widest pt-8 border-t border-white/5">
          This portal grants read-only access to executed documents and the legacy note.<br/>
          Nothing else on the account is visible from here.
        </div>
      </div>
    </div>
  );
}
